// The wheel is HONEST THEATER. The outcome is already sealed and the FS has already moved
// before a single frame animates; spinTo() just eases the wheel to the wedge the server
// already returned. There is no client-side RNG here and there must never be a fake
// near-miss — the result is pre-decided, so faking a "so close" is a literal lie.
//
// Wedges are EQUAL — a deliberate ruling (2026-07-26, operator play-test reversal of the
// proportional experiment): the wheel is honest THEATER, not an odds display. Proportional
// geometry rendered a loss pie-chart (a 55% brick slab saying "you will lose") and made the
// thin gold sliver a permanent near-miss magnet. The truth lives where numbers belong: the
// legend row directly under the canvas prints the REAL published percentages, /wheel_odds
// publishes the table, and the verify flow recomputes every spin. Equal spread restores the
// prize-wheel fantasy WITHOUT touching a single odd. Drama-suppression stays: tick cadence
// uniform per boundary, easing identical for every outcome, spin duration a compile-time
// constant for all tiers (a stopwatch-detectable tell would torch the provably-fair pitch).
//
// All randomness in this file is pure theater — landing jitter INSIDE the already-decided
// wedge, never across a boundary. It can never change which wedge the pointer reads.
//
// golden_ticket (230 ppm) gets NO wedge: a 7° floor on a 0.083°-true slice would be a 72×
// geometric lie. It lives as a hairline shimmer on the rim ("a rumor, not a target") and,
// on that outcome, the rim itself resolves the spin — an explicit branch, never the
// missing-wedge fallback.

import { tierLabel } from './copy';

const COLORS: Record<string, string> = {
  nothing: '#D2543F', // Erisian Brick — the offering / the Void
  fs_5_25: '#F5B301', // Molten Gold — FlaxScript
  spin_ticket: '#39E58C', // Arcane Green — another spin
  common_item: '#282B30', // Lit Stone — a plain trinket
  jackpot: '#FFD34E', // Bright Gold — the pot
  golden_ticket: '#F5B301', // fallback layouts only (normally rim-resolved, no wedge)
};

const STONE = '#141518';
const BORDER = '#3A3E44';
const TEXT = '#EDEDE6';
const GOLD_HI = '#FFD34E';
const GOLD_LO = '#C8860A';

// --- constants (exported for the constant-duration regression test) ---
// Duration and turns are PAIRED (showrunner invariant: peak launch velocity ≈ 33 rad/s —
// stretch duration without adding turns and the shove reads weaker, not longer). Cubic
// easing replaces quart: quart froze the last ~10% of the ride into a dead tail (the
// original "after-rolling is weird"); cubic keeps the wheel visibly creeping to the end.
export const SPIN_DURATION_MS = 5200; // main deceleration — identical for EVERY tier
export const SETTLE_MS = 420; // overshoot settle window (golden: bloom rides it, post-rest)
export const TOTAL_SPIN_MS = SPIN_DURATION_MS + SETTLE_MS;
export const REDUCED_TOTAL_MS = 600; // prefers-reduced-motion path (also tier-constant)
export const REVEAL_BEAT_MS = 200; // state.ts waits this after resolve before the card
export const SPIN_TURNS = 9; // constant full turns (paired with the duration — see above)
const WINDUP_MAX_RAD = 0.14; // ≤8° pull-back during the settle round-trip; a gesture, not a signal
const LABEL_MIN_ARC = (18 * Math.PI) / 180; // below this, color + legend carry the meaning
const POINTER = -Math.PI / 2; // top of the canvas
const MAX_OSC_AMPLITUDE = 0.08; // radians
const OSC_CYCLES = 2;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

// Idle "pulse": the wheel breathes (scale only — NEVER rotation, the resting pointer must
// keep reading the true landed wedge) and a glint sweeps the rim. Throttled, stops when the
// tab hides, goes fully still after a minute, and never runs under reduced motion. No idle
// audio, and nothing escalates the longer you refrain from spinning — attract-as-pressure
// is banned (wheel_ux OUT-list).
const IDLE_FRAME_MS = 50; // ~20fps
const IDLE_MAX_MS = 60_000;
const BREATH_PERIOD_MS = 4_000;
const BREATH_AMPLITUDE = 0.004;
const GLINT_PERIOD_MS = 9_000;

// Shade a #RRGGBB color toward black (factor < 1) or white (factor > 1) — the procedural
// wedge gradient runs base color at the rim into a darkened center.
function shade(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const ch = (v: number) =>
    Math.max(0, Math.min(255, Math.round(factor >= 1 ? v + (255 - v) * (factor - 1) : v * factor)));
  const r = ch((n >> 16) & 255);
  const g = ch((n >> 8) & 255);
  const b = ch(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

export interface WheelEntry {
  tier: string;
  weightPpm: number;
}

export interface WedgeLayout {
  tier: string;
  start: number; // radians in wheel frame, [0, 2π), clockwise
  end: number;
}

export interface SpinPlan {
  endRotation: number;
  oscAmplitude: number; // 0 = no overshoot (golden / reduced motion)
  oscCycles: number;
  durationMs: number; // main phase
  settleMs: number;
  golden: boolean;
}

// Pure: the mode's tiers → EQUAL wedge arcs, golden_ticket INCLUDED as a full equal wedge
// (2026-07-26 night ruling: under equal spread, a differently-sized wedge is the only kind
// that can lie — and a wedge-less golden left the resting pointer disagreeing with the
// result card on golden landings, the one thing the pointer must never do). Weights are
// validated only as a sanity check on the config (the arcs don't use them — the legend
// does). Throws on malformed input — the caller falls back to equal wedges over the raw
// tier names (render must never block a spin).
export function computeLayout(entries: WheelEntry[]): WedgeLayout[] {
  const wedged = entries;
  if (wedged.length === 0) throw new Error('no wedge tiers');
  for (const e of wedged) {
    if (!Number.isFinite(e.weightPpm) || e.weightPpm <= 0) {
      throw new Error(`bad weight for ${e.tier}`);
    }
  }
  const arc = (Math.PI * 2) / wedged.length;
  const layout: WedgeLayout[] = wedged.map((e, i) => ({
    tier: e.tier,
    start: i * arc,
    end: (i + 1) * arc,
  }));
  // Absorb float dust so the last edge meets 2π exactly.
  layout[layout.length - 1].end = Math.PI * 2;
  return layout;
}

// Pure: where and how the spin ends. `rand` ∈ [0,1) drives only the in-wedge landing jitter
// (≤35% of the half-arc — always safely inside the decided wedge). Duration is constant
// across all tiers by construction; the regression test asserts it.
export function planSpin(
  layout: WedgeLayout[],
  targetTier: string,
  currentRotation: number,
  rand: () => number,
  reducedMotion = false,
): SpinPlan {
  const durationMs = reducedMotion ? REDUCED_TOTAL_MS : SPIN_DURATION_MS;
  const settleMs = reducedMotion ? 0 : SETTLE_MS;
  const wedge = layout.find((w) => w.tier === targetTier);

  if (!wedge) {
    // Defensive only (every reachable tier owns an equal wedge now): free-stop after the
    // same constant turns/duration; the result card carries the truth.
    return {
      endRotation: currentRotation + Math.PI * 2 * SPIN_TURNS,
      oscAmplitude: 0,
      oscCycles: 0,
      durationMs,
      settleMs,
      golden: targetTier === 'golden_ticket',
    };
  }

  const halfArc = (wedge.end - wedge.start) / 2;
  const center = (wedge.start + wedge.end) / 2;
  const jitter = (rand() * 2 - 1) * 0.35 * halfArc;
  const landing = center + jitter;
  // Pointer reads wheel-frame angle a when rotation ≡ −a (mod 2π).
  const endRotation = normalizeToward(currentRotation, -landing) + Math.PI * 2 * SPIN_TURNS;
  const boundaryDistance = Math.min(landing - wedge.start, wedge.end - landing);
  const oscAmplitude = reducedMotion
    ? 0
    : Math.min(MAX_OSC_AMPLITUDE, 0.8 * boundaryDistance);
  return {
    endRotation,
    oscAmplitude,
    oscCycles: reducedMotion ? 0 : OSC_CYCLES,
    durationMs,
    settleMs,
    golden: targetTier === 'golden_ticket',
  };
}

// Pure: boundaries swept past the pointer while rotation moves r1 → r2 (r2 ≥ r1). Exact for
// unequal wedges: boundary b sits under the pointer whenever rotation ≡ −b (mod 2π).
export function countBoundariesCrossed(
  boundaries: number[],
  r1: number,
  r2: number,
): number {
  const twoPi = Math.PI * 2;
  let crossed = 0;
  for (const b of boundaries) {
    crossed += Math.floor((r2 + b) / twoPi) - Math.floor((r1 + b) / twoPi);
  }
  return crossed;
}

export interface SpinOptions {
  // Uniform per-boundary tick: `crossed` boundaries swept this frame at `velocity` rad/s.
  // NO per-wedge information is exposed — cadence and pitch derive from geometry alone.
  onTick?: (crossed: number, velocity: number) => void;
}

export class Wheel {
  private ctx: CanvasRenderingContext2D;
  private rotation = 0; // radians
  private layout: WedgeLayout[] = [];
  private boundaries: number[] = [];
  private spinning = false;
  private goldenPhase = 0; // 0 = normal · 1 = full rim bloom (golden finale)
  private pre: HTMLCanvasElement | null = null;
  private noise: HTMLCanvasElement | null = null;
  private size = 360; // device-pixel backing size
  private readonly onResize = () => this.resize();
  private readonly onActivity = () => this.notifyActivity();
  private readonly onVisibility = () => {
    if (typeof document !== 'undefined' && !document.hidden) this.notifyActivity();
  };
  // Idle engine state.
  private idleRaf = 0;
  private idleLastFrame = 0;
  private idleStart = 0;
  private lastActivity = 0;
  private breathScale = 1;
  private glintAngle: number | null = null;

  constructor(
    private canvas: HTMLCanvasElement,
    entries: WheelEntry[],
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d context unavailable');
    this.ctx = ctx;
    this.setLayout(entries);
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize);
      window.addEventListener('orientationchange', this.onResize);
      window.addEventListener('pointerdown', this.onActivity, { capture: true });
      window.addEventListener('keydown', this.onActivity, { capture: true });
      document.addEventListener('visibilitychange', this.onVisibility);
    }
  }

  destroy(): void {
    this.stopIdle();
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      window.removeEventListener('pointerdown', this.onActivity, { capture: true });
      window.removeEventListener('keydown', this.onActivity, { capture: true });
      document.removeEventListener('visibilitychange', this.onVisibility);
    }
  }

  // --- idle pulse ---
  private reducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
    );
  }

  startIdle(): void {
    if (this.idleRaf || this.spinning || this.reducedMotion()) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    const now = performance.now();
    this.idleStart = now;
    this.idleLastFrame = 0;
    this.lastActivity = now; // every (re)start counts as fresh activity
    const frame = (ts: number) => {
      this.idleRaf = 0;
      if (
        this.spinning ||
        this.reducedMotion() || // honored live, not only at start
        (typeof document !== 'undefined' && document.hidden)
      ) {
        this.settleIdleFrame();
        return;
      }
      if (ts - this.lastActivity > IDLE_MAX_MS) {
        // A minute of stillness: go fully still (battery, and no attract-pressure).
        this.settleIdleFrame();
        return;
      }
      if (ts - this.idleLastFrame >= IDLE_FRAME_MS) {
        this.idleLastFrame = ts;
        const t = ts - this.idleStart;
        this.breathScale = 1 + BREATH_AMPLITUDE * Math.sin((t / BREATH_PERIOD_MS) * Math.PI * 2);
        // The glint sweeps the rim in SCREEN frame — decorative light, keyed to nothing.
        const phase = (t % GLINT_PERIOD_MS) / GLINT_PERIOD_MS;
        this.glintAngle = phase < 0.22 ? (phase / 0.22) * Math.PI * 2 : null;
        this.draw();
      }
      this.idleRaf = requestAnimationFrame(frame);
    };
    this.idleRaf = requestAnimationFrame(frame);
  }

  stopIdle(): void {
    if (this.idleRaf) {
      cancelAnimationFrame(this.idleRaf);
      this.idleRaf = 0;
    }
    this.settleIdleFrame();
  }

  private settleIdleFrame(): void {
    if (this.breathScale !== 1 || this.glintAngle !== null) {
      this.breathScale = 1;
      this.glintAngle = null;
      this.draw();
    }
  }

  notifyActivity(): void {
    this.lastActivity = performance.now();
    if (!this.idleRaf && !this.spinning) this.startIdle();
  }

  // --- wind-up: fills the settle round-trip with a held pull-back ---
  // A gesture, not a signal: same size every spin regardless of outcome, and clamped so
  // the resting pointer can never read across a boundary while pulled.
  private windUpOffset = 0;

  windUp(): void {
    if (this.spinning || this.windUpOffset !== 0) return;
    this.stopIdle();
    const twoPi = Math.PI * 2;
    const a = ((-this.rotation % twoPi) + twoPi) % twoPi; // wheel-frame angle under pointer
    const wedge = this.layout.find((w) => a >= w.start - 1e-9 && a < w.end + 1e-9);
    const room = wedge ? Math.max(0, wedge.end - a - 0.02) : WINDUP_MAX_RAD;
    const pull = Math.min(WINDUP_MAX_RAD, room);
    if (pull <= 0) return;
    this.windUpOffset = pull;
    const start = this.rotation;
    const t0 = performance.now();
    const frame = (now: number) => {
      if (this.spinning || this.windUpOffset === 0) return; // spin or release took over
      const t = Math.min(1, Math.max(0, now - t0) / 260);
      this.rotation = start - pull * easeOutCubic(t);
      this.draw();
      if (t < 1) requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  // A refused/failed settle eases the wheel back to its rest — no spin, no drama.
  releaseWindUp(): void {
    if (this.windUpOffset === 0 || this.spinning) {
      this.windUpOffset = 0;
      return;
    }
    const pull = this.windUpOffset;
    this.windUpOffset = 0;
    const start = this.rotation;
    const t0 = performance.now();
    const frame = (now: number) => {
      if (this.spinning) return;
      const t = Math.min(1, Math.max(0, now - t0) / 200);
      this.rotation = start + pull * easeOutCubic(t);
      this.draw();
      if (t < 1) requestAnimationFrame(frame);
      else this.notifyActivity();
    };
    requestAnimationFrame(frame);
  }

  // --- resolution glow: the won tier's color, POST-REST only (confirmation, not prophecy) ---
  private glowColor: string | null = null;
  private glowStart = 0;

  revealGlow(tier: string): void {
    this.glowColor = COLORS[tier] ?? null;
    this.glowStart = performance.now();
    this.draw();
    this.notifyActivity(); // the idle loop repaints the fade
  }

  // Per-mode relayout (paid and free tables differ). Falls back to equal wedges over ALL
  // given tiers (including golden_ticket, which then owns a real wedge and is spun to
  // normally) if the ppm data is malformed.
  //
  // While a spin is in flight the relayout is DEFERRED: the running plan's endRotation was
  // computed against the current geometry, and swapping wedge boundaries under it would let
  // the pointer come to rest on a wedge that is not the settled tier. The pending layout
  // applies the moment the spin resolves. (state.ts also gates mode switches while busy —
  // this is the belt-and-suspenders.)
  private pendingEntries: WheelEntry[] | null = null;

  setLayout(entries: WheelEntry[]): void {
    if (this.spinning) {
      this.pendingEntries = entries;
      return;
    }
    try {
      this.layout = computeLayout(entries);
    } catch {
      const tiers = entries.length ? entries.map((e) => e.tier) : ['nothing'];
      const arc = (Math.PI * 2) / tiers.length;
      this.layout = tiers.map((tier, i) => ({ tier, start: i * arc, end: (i + 1) * arc }));
    }
    this.boundaries = this.layout.map((w) => w.start);
    this.resize(); // prerenders + draws
    this.startIdle(); // idempotent; the machine has a pulse from the first frame
  }

  hasWedge(tier: string): boolean {
    return this.layout.some((w) => w.tier === tier);
  }

  // DPR-aware backing store sized from the CSS box (styles.css gives the canvas
  // width:min(360px,100%) + aspect-ratio:1). Blurry-phone fix.
  private resize(): void {
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const rect = this.canvas.getBoundingClientRect();
    const css = rect.width > 0 ? rect.width : 360;
    const size = Math.max(1, Math.round(css * dpr));
    if (size !== this.size || this.canvas.width !== size) {
      this.size = size;
      this.canvas.width = size;
      this.canvas.height = size;
    }
    this.prerender();
    this.draw();
  }

  // Static wheel image (wedges + labels + rim rumor) drawn once per layout/size; each frame
  // is then clear + rotate + drawImage — the perf backbone for the Discord iframe.
  private prerender(): void {
    const size = this.size;
    const pre = document.createElement('canvas');
    pre.width = size;
    pre.height = size;
    const ctx = pre.getContext('2d');
    if (!ctx) return;
    const c = size / 2;
    const r = size / 2 - Math.max(8, size * 0.022);
    const scale = size / 360; // typography tracks the backing size

    for (const wedge of this.layout) {
      const start = wedge.start + POINTER;
      const end = wedge.end + POINTER;
      ctx.beginPath();
      ctx.moveTo(c, c);
      ctx.arc(c, c, r, start, end);
      ctx.closePath();
      // Procedural depth: base color at the rim falling into a darkened center — the
      // zero-asset stand-in for painted wedges until the ComfyUI art pass.
      const base = COLORS[wedge.tier] ?? '#1E2024';
      const gradient = ctx.createRadialGradient(c, c, r * 0.15, c, c, r);
      gradient.addColorStop(0, shade(base, 0.62));
      gradient.addColorStop(0.75, base);
      gradient.addColorStop(1, shade(base, 1.08));
      ctx.fillStyle = gradient;
      ctx.fill();
      ctx.strokeStyle = BORDER;
      ctx.lineWidth = 2 * scale;
      ctx.stroke();

      if (wedge.end - wedge.start >= LABEL_MIN_ARC) {
        ctx.save();
        ctx.translate(c, c);
        ctx.rotate((wedge.start + wedge.end) / 2 + POINTER);
        ctx.textAlign = 'right';
        ctx.fillStyle = wedge.tier === 'common_item' ? TEXT : STONE;
        ctx.font = `600 ${Math.round(13 * scale)}px system-ui, sans-serif`;
        // Golden carries its apple, same size and treatment as every other label —
        // visible ≠ special (no glow, no tick change, nothing keyed to it mid-spin).
        const label = wedge.tier === 'golden_ticket' ? '🍎 ' + tierLabel(wedge.tier) : tierLabel(wedge.tier);
        ctx.fillText(label, r - 12 * scale, 5 * scale);
        ctx.restore();
      }
    }

    // Grain: a tiled noise pattern over the whole disc — stone, not vector.
    ctx.save();
    ctx.beginPath();
    ctx.arc(c, c, r, 0, Math.PI * 2);
    ctx.clip();
    const noisePattern = ctx.createPattern(this.noiseTile(), 'repeat');
    if (noisePattern) {
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = noisePattern;
      ctx.fillRect(0, 0, size, size);
    }
    ctx.restore();

    // The rim: a quiet gold ring, plus one brighter hairline segment — the golden rumor.
    // It rotates with the wheel and points at nothing; kallisti.
    ctx.beginPath();
    ctx.arc(c, c, r + 3 * scale, 0, Math.PI * 2);
    ctx.strokeStyle = GOLD_LO;
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = 1.5 * scale;
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(c, c, r + 3 * scale, 0.9, 0.95);
    ctx.strokeStyle = GOLD_HI;
    ctx.lineWidth = 2 * scale;
    ctx.stroke();

    this.pre = pre;
  }

  private noiseTile(): HTMLCanvasElement {
    if (this.noise) return this.noise;
    const tile = document.createElement('canvas');
    tile.width = 64;
    tile.height = 64;
    const ctx = tile.getContext('2d');
    if (ctx) {
      const img = ctx.createImageData(64, 64);
      for (let i = 0; i < img.data.length; i += 4) {
        const v = Math.floor(Math.random() * 255);
        img.data[i] = v;
        img.data[i + 1] = v;
        img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
      ctx.putImageData(img, 0, 0);
    }
    this.noise = tile;
    return tile;
  }

  private draw(): void {
    const { ctx } = this;
    const size = this.size;
    const c = size / 2;
    const r = size / 2 - Math.max(8, size * 0.022);
    const scale = size / 360;
    ctx.clearRect(0, 0, size, size);

    if (this.pre) {
      ctx.save();
      ctx.translate(c, c);
      if (this.breathScale !== 1) ctx.scale(this.breathScale, this.breathScale);
      ctx.rotate(this.rotation);
      ctx.drawImage(this.pre, -c, -c);
      ctx.restore();
    }

    // Idle glint: a soft light sweeping the rim in screen frame — pure decoration,
    // keyed to nothing the wheel could land on.
    if (this.glintAngle !== null) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(c, c, r + 3 * scale, this.glintAngle - 0.12, this.glintAngle + 0.12);
      ctx.strokeStyle = GOLD_HI;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 2.5 * scale;
      ctx.stroke();
      ctx.restore();
    }

    // Golden finale: the wedges recede, the rim carries the answer.
    if (this.goldenPhase > 0) {
      ctx.save();
      ctx.globalAlpha = 0.72 * this.goldenPhase;
      ctx.beginPath();
      ctx.arc(c, c, r + 1 * scale, 0, Math.PI * 2);
      ctx.fillStyle = STONE;
      ctx.fill();
      ctx.globalAlpha = this.goldenPhase;
      ctx.beginPath();
      ctx.arc(c, c, r + 3 * scale, 0, Math.PI * 2);
      ctx.strokeStyle = GOLD_HI;
      ctx.lineWidth = (2 + 6 * this.goldenPhase) * scale;
      ctx.stroke();
      ctx.restore();
    }

    // Resolution glow: a soft hub ring in the WON tier's color, fading over ~900ms.
    if (this.glowColor) {
      const age = performance.now() - this.glowStart;
      const GLOW_MS = 900;
      if (age > GLOW_MS) {
        this.glowColor = null;
      } else {
        ctx.save();
        ctx.beginPath();
        ctx.arc(c, c, 26 * scale, 0, Math.PI * 2);
        ctx.strokeStyle = this.glowColor;
        ctx.globalAlpha = 0.5 * (1 - age / GLOW_MS);
        ctx.lineWidth = 6 * scale;
        ctx.stroke();
        ctx.restore();
      }
    }

    // Hub, with an inner shadow — a socket, not a sticker.
    ctx.beginPath();
    ctx.arc(c, c, 18 * scale, 0, Math.PI * 2);
    ctx.fillStyle = STONE;
    ctx.fill();
    // Soft socket via stepped concentric strokes — ctx.filter blur is missing on older
    // WebKit (iOS Discord webviews), and an inert filter would draw a crisp band instead.
    ctx.save();
    for (const [radius, alpha] of [
      [16, 0.3],
      [15, 0.22],
      [14, 0.14],
      [13, 0.08],
    ] as const) {
      ctx.beginPath();
      ctx.arc(c, c, radius * scale, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(0,0,0,${alpha})`;
      ctx.lineWidth = 2.2 * scale;
      ctx.stroke();
    }
    ctx.restore();
    ctx.beginPath();
    ctx.arc(c, c, 18 * scale, 0, Math.PI * 2);
    ctx.strokeStyle = '#F5B301';
    ctx.lineWidth = 2 * scale;
    ctx.stroke();

    // Pointer at top.
    ctx.beginPath();
    ctx.moveTo(c - 10 * scale, c - r - 2 * scale);
    ctx.lineTo(c + 10 * scale, c - r - 2 * scale);
    ctx.lineTo(c, c - r + 14 * scale);
    ctx.closePath();
    ctx.fillStyle = '#F5B301';
    ctx.fill();
  }

  // Ease the wheel to the server-returned tier. Constant duration for every tier; uniform
  // boundary ticks; damped in-wedge overshoot; golden_ticket resolves on the rim.
  spinTo(targetTier: string, opts: SpinOptions = {}): Promise<void> {
    if (this.spinning) return Promise.resolve();
    // A relayout deferred during the previous spin applies now, BEFORE this spin plans —
    // never at spin-stop, where it would change what the resting pointer reads mid-reveal.
    if (this.pendingEntries) {
      const entries = this.pendingEntries;
      this.pendingEntries = null;
      this.setLayout(entries);
    }
    this.spinning = true;
    this.stopIdle();
    this.windUpOffset = 0; // the pull releases into the spin
    this.glowColor = null;
    this.goldenPhase = 0;

    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
    const plan = planSpin(this.layout, targetTier, this.rotation, Math.random, reduced);
    if (targetTier !== 'golden_ticket' && !this.hasWedge(targetTier)) {
      // Defensive only — every reachable outcome tier is mounted per mode. Log loudly; the
      // free-stop plan above keeps the theater from contradicting the sealed result.
      console.error(`[wheel] outcome tier "${targetTier}" has no wedge; check the mount set`);
    }

    const start = this.rotation;
    const delta = plan.endRotation - start;

    return new Promise<void>((resolve) => {
      const startTs = performance.now();
      let prevRotation = start;
      let prevTs = startTs;

      const frame = (now: number) => {
        // Floor at 0: the first rAF timestamp (vsync start) can precede the
        // performance.now() captured above, and a negative t would extrapolate the
        // easing into a one-frame backward kick with duplicated ticks.
        const elapsed = Math.max(0, now - startTs);
        const t = Math.min(1, elapsed / plan.durationMs);
        if (t < 1) {
          this.rotation = start + delta * easeOutCubic(t);
        } else {
          // Settle window: damped overshoot around the landing (or, golden, a held stop
          // while the rim blooms). Amplitude is clamped below the boundary distance, so the
          // pointer can never read a neighboring wedge — not even for a frame.
          const s = Math.min(1, (elapsed - plan.durationMs) / Math.max(1, plan.settleMs));
          const damped =
            plan.oscAmplitude *
            Math.sin(s * plan.oscCycles * Math.PI * 2) *
            (1 - s);
          this.rotation = plan.endRotation + damped;
        }

        // Golden bloom fires POST-REST only (2026-07-26 ruling — the mid-spin exception is
        // deleted; golden owns a real wedge now, so the bloom is confirmation, never
        // prophecy): it ramps through the settle window, after the pointer is legible.
        if (plan.golden && elapsed > plan.durationMs) {
          this.goldenPhase = Math.min(1, (elapsed - plan.durationMs) / Math.max(1, plan.settleMs));
        }

        const dtSeconds = Math.max(1e-6, (now - prevTs) / 1000);
        const crossed = countBoundariesCrossed(this.boundaries, prevRotation, this.rotation);
        if (crossed > 0) opts.onTick?.(crossed, (this.rotation - prevRotation) / dtSeconds);
        prevRotation = this.rotation;
        prevTs = now;

        this.draw();

        if (elapsed < plan.durationMs + plan.settleMs) {
          requestAnimationFrame(frame);
        } else {
          const twoPi = Math.PI * 2;
          this.rotation = ((plan.endRotation % twoPi) + twoPi) % twoPi;
          this.draw();
          this.spinning = false;
          this.notifyActivity(); // the pulse resumes; the resting wedge never rotates
          resolve();
        }
      };
      requestAnimationFrame(frame);
    });
  }

  clearGolden(): void {
    this.goldenPhase = 0;
    this.draw();
  }
}

// Rotate `target` forward relative to `from` so we always ease clockwise-forward, never snap.
function normalizeToward(from: number, target: number): number {
  const twoPi = Math.PI * 2;
  const base = Math.floor(from / twoPi) * twoPi;
  let candidate = base + (((target % twoPi) + twoPi) % twoPi);
  while (candidate < from) candidate += twoPi;
  return candidate;
}
